<template>
  <div class="user-page">
    <header class="page-heading">
      <div>
        <span class="eyebrow">{{ translate('Управление пользователями', 'user_management') }}</span>
        <h1>{{ translate('Карточка пользователя', 'user_card') }}</h1>
        <p>{{ translate('Профиль, состояние учётной записи, роли и разрешения', 'user_card_description') }}</p>
      </div>
      <div class="heading-actions">
        <v-btn outlined color="primary" :disabled="!user.id || !canEdit" @click="changePassword">
          <v-icon left small>mdi-shield-key-outline</v-icon>
          {{ translate('Изменить пароль', 'change_password') }}
        </v-btn>
        <v-btn color="primary" depressed :disabled="!user.id || !canEdit" @click="openDialog(user, 'CtlgUser')">
          <v-icon left small>mdi-pencil-outline</v-icon>
          {{ translate('Редактировать', 'edit_button') }}
        </v-btn>
      </div>
    </header>

    <section class="profile-card surface-card">
      <div class="profile-main">
        <div class="avatar-shell" aria-hidden="true">
          <span>{{ initials }}</span>
          <i :class="{'avatar-status--blocked': user.blocked}"></i>
        </div>
        <div class="profile-identity">
          <div class="identity-title">
            <h2>{{ displayName }}</h2>
            <span class="status-pill" :class="user.blocked ? 'status-pill--blocked' : 'status-pill--active'">
              <v-icon x-small>{{ user.blocked ? 'mdi-lock-outline' : 'mdi-check-circle-outline' }}</v-icon>
              {{ user.blocked ? translate('Заблокирован', 'blocked') : translate('Активен', 'active') }}
            </span>
          </div>
          <strong class="username">@{{ user.username || '—' }}</strong>
          <div class="identity-meta">
            <span><v-icon small>mdi-identifier</v-icon>ID {{ user.id || '—' }}</span>
            <span><v-icon small>mdi-map-marker-outline</v-icon>{{ distributionPointName }}</span>
            <span><v-icon small>mdi-account-key-outline</v-icon>{{ roles.length }} {{ translate('ролей', 'roles_count') }}</span>
          </div>
        </div>
      </div>

      <div class="profile-details">
        <article class="detail-group">
          <div class="detail-icon detail-icon--teal"><v-icon>mdi-card-account-details-outline</v-icon></div>
          <div class="detail-content">
            <span class="detail-label">{{ translate('Личные данные', 'personal_data') }}</span>
            <dl>
              <div><dt>{{ translate('Дата рождения', 'dateBirth', 'CtlgUser') }}</dt><dd>{{ formatDate(user.dateBirth) }}</dd></div>
              <div><dt>{{ translate('Мобильный телефон', 'mobileNumber', 'CtlgUser') }}</dt><dd>{{ user.mobileNumber || '—' }}</dd></div>
              <div><dt>{{ translate('Рабочий телефон', 'workNumber', 'CtlgUser') }}</dt><dd>{{ user.workNumber || '—' }}</dd></div>
            </dl>
          </div>
        </article>

        <article class="detail-group">
          <div class="detail-icon detail-icon--blue"><v-icon>mdi-login-variant</v-icon></div>
          <div class="detail-content">
            <span class="detail-label">{{ translate('Авторизация', 'authorization') }}</span>
            <dl>
              <div><dt>{{ translate('Последний вход', 'lastLogin', 'CtlgUser') }}</dt><dd>{{ formatDate(user.lastLogin, true) }}</dd></div>
              <div><dt>{{ translate('Последняя попытка', 'lastAttempt', 'CtlgUser') }}</dt><dd>{{ formatDate(user.lastAttempt, true) }}</dd></div>
              <div><dt>{{ translate('Неудачных попыток', 'numberAttempts', 'CtlgUser') }}</dt><dd>{{ user.numberAttempts || 0 }}</dd></div>
            </dl>
          </div>
        </article>

        <article class="detail-group">
          <div class="detail-icon detail-icon--violet"><v-icon>mdi-shield-account-outline</v-icon></div>
          <div class="detail-content">
            <span class="detail-label">{{ translate('Параметры доступа', 'access_settings') }}</span>
            <dl>
              <div><dt>{{ translate('Проверка в AD', 'checkInAd', 'CtlgUser') }}</dt><dd>{{ yesNo(user.checkInAd) }}</dd></div>
              <div><dt>{{ translate('Действителен до', 'expireLogin', 'CtlgUser') }}</dt><dd>{{ formatDate(user.expireLogin, true) }}</dd></div>
              <div><dt>{{ translate('Точка распределения', 'distributionPoint', 'CtlgUser') }}</dt><dd>{{ distributionPointName }}</dd></div>
            </dl>
          </div>
        </article>
      </div>
    </section>

    <section class="summary-grid">
      <article class="summary-card">
        <div class="summary-icon summary-icon--teal"><v-icon>mdi-account-group-outline</v-icon></div>
        <div><span>{{ translate('Назначено ролей', 'assigned_roles') }}</span><strong>{{ roles.length }}</strong></div>
      </article>
      <article class="summary-card">
        <div class="summary-icon summary-icon--blue"><v-icon>mdi-shield-check-outline</v-icon></div>
        <div><span>{{ translate('Разрешений', 'permissions') }}</span><strong>{{ privileges.length }}</strong></div>
      </article>
      <article class="summary-card">
        <div class="summary-icon" :class="user.blocked ? 'summary-icon--red' : 'summary-icon--green'"><v-icon>{{ user.blocked ? 'mdi-lock-outline' : 'mdi-account-check-outline' }}</v-icon></div>
        <div><span>{{ translate('Состояние доступа', 'access_status') }}</span><strong class="summary-status">{{ user.blocked ? translate('Заблокирован', 'blocked') : translate('Разрешён', 'allowed') }}</strong></div>
      </article>
      <article class="summary-card">
        <div class="summary-icon summary-icon--orange"><v-icon>mdi-clock-outline</v-icon></div>
        <div><span>{{ translate('Последняя активность', 'last_activity') }}</span><strong class="summary-date">{{ formatDate(user.lastLogin, true) }}</strong></div>
      </article>
    </section>

    <section class="access-grid">
      <article class="access-panel surface-card roles-panel">
        <header class="panel-heading">
          <div>
            <span class="eyebrow">{{ translate('Доступ', 'access') }}</span>
            <h2>{{ translate('Роли пользователя', 'roles') }}</h2>
            <p>{{ translate('Наборы разрешений, назначенные учётной записи', 'user_roles_description') }}</p>
          </div>
          <span class="count-badge">{{ roles.length }}</span>
        </header>
        <sm-simple-table
            :key="'CtlgRole' + dialogReadyCounter"
            :entity="'CtlgRole'"
            :entity-id="Number($route.params.id)"
            :editable="canEdit"
        />
      </article>

      <article class="access-panel surface-card permissions-panel">
        <header class="panel-heading">
          <div>
            <span class="eyebrow">{{ translate('Безопасность', 'security') }}</span>
            <h2>{{ translate('Эффективные разрешения', 'permissions') }}</h2>
            <p>{{ translate('Итоговые права, полученные через назначенные роли', 'user_permissions_description') }}</p>
          </div>
          <span class="count-badge count-badge--blue">{{ privileges.length }}</span>
        </header>
        <sm-simple-table
            :key="'CtlgPrivilege' + dialogReadyCounter"
            :entity="'CtlgPrivilege'"
            :entity-id="Number($route.params.id)"
            :editable="false"
        />
      </article>
    </section>

    <sm-password-dialog />
  </div>
</template>

<script>
const getApi = () => import('@/services/Api');

export default {
  name: 'user-page',
  components: {
    'sm-password-dialog': () => import('@/components/PasswordDialog.vue'),
    'sm-simple-table': () => import('@/components/BaseSimpleDataTable'),
  },
  data: () => ({user: {}}),
  computed: {
    dialogReadyCounter() { return this.$store.getters.getDialogReadyCounter; },
    roles() { return this.$store.getters.getSdtRowsByEntity('CtlgRole') || []; },
    privileges() { return this.$store.getters.getSdtRowsByEntity('CtlgPrivilege') || []; },
    canEdit() { return this.$store.getters.isPerimitted('CtlgUser', 'edit'); },
    displayName() {
      return [this.user.lname, this.user.fname].filter(Boolean).join(' ') || this.user.username || this.translate('Пользователь', 'user');
    },
    initials() {
      const value = [this.user.fname, this.user.lname].filter(Boolean).map(item => String(item).trim().charAt(0)).join('');
      return value.toUpperCase() || 'U';
    },
    distributionPointName() { return this.referenceValue(this.user.distributionPoint) || '—'; },
  },
  watch: {
    dialogReadyCounter() { this.getUserInfo(this.$route.params.id); },
    '$route.params.id'(value) { if (value) this.getUserInfo(value); },
  },
  created() {
    this.getUserInfo(this.$route.params.id);
    document.querySelector('html').style.overflowY = 'auto';
  },
  methods: {
    translate(def, code, entity) { return this.$store.getters.getTranslation(def, code, entity); },
    referenceValue(value) {
      if (!value) return '';
      if (typeof value !== 'object') return String(value);
      const lang = (this.$store.getters.getChosenLang || 'rus').toLowerCase();
      const name = value.name || value;
      return name[lang] || name.value || value.value || value.code || '';
    },
    formatDate(value, withTime = false) {
      if (!value) return '—';
      return this.$store.getters.formatDate(value, withTime ? 1 : undefined) || '—';
    },
    yesNo(value) { return value ? this.translate('Да', 'yes') : this.translate('Нет', 'no'); },
    changePassword() { this.$store.dispatch('storePasswordDialog', {val: true}); },
    async getUserInfo(userId) {
      const {http} = await getApi();
      const response = await http.getData('view/CtlgUser/', {where: [{value: 'id', text: userId}]});
      if (response.data.tabRows && response.data.tabRows.length > 0) this.user = response.data.tabRows[0];
    },
    async openDialog(item, table) {
      await this.$store.dispatch('storeProgress', {text: 'downloading'});
      await this.$store.dispatch('storeDialogParams', {action: 'edit', initiator: table, editedItemId: item.id});
      await this.$store.dispatch('storeDialogTable', {table});
      await this.$store.dispatch('storeProgress', {text: null});
      this.$store.dispatch('storeDialog', {val: true});
    },
  },
};
</script>

<style scoped>
.user-page{padding:18px;color:#18344d;background:linear-gradient(180deg,#f4f8fb 0,#f8fafc 280px);min-height:100%}.page-heading{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;margin-bottom:16px}.page-heading h1{margin:4px 0 3px;font-size:28px;line-height:1.2}.page-heading p,.panel-heading p{margin:0;color:#7a8da0}.eyebrow{color:#07888a;font-size:10px;font-weight:800;letter-spacing:.13em;text-transform:uppercase}.heading-actions{display:flex;gap:9px}.surface-card{border:1px solid #dfe9ef;border-radius:14px;background:#fff;box-shadow:0 5px 18px rgba(38,70,94,.05)}.profile-card{overflow:hidden}.profile-main{display:flex;align-items:center;gap:18px;padding:22px 24px;border-bottom:1px solid #e7eef3;background:linear-gradient(110deg,#fff 40%,#f0faf9)}.avatar-shell{position:relative;display:grid;place-items:center;flex:0 0 auto;width:78px;height:78px;border:7px solid #e5f4f3;border-radius:50%;color:#fff;font-size:24px;font-weight:800;background:linear-gradient(145deg,#078b8c,#1767a4);box-shadow:0 4px 12px rgba(17,112,134,.18)}.avatar-shell i{position:absolute;right:-1px;bottom:2px;width:16px;height:16px;border:3px solid #fff;border-radius:50%;background:#16a66b}.avatar-shell i.avatar-status--blocked{background:#e14b55}.profile-identity{min-width:0}.identity-title{display:flex;align-items:center;flex-wrap:wrap;gap:11px}.identity-title h2{margin:0;font-size:24px}.username{display:block;margin-top:3px;color:#16878a;font-size:13px}.status-pill{display:inline-flex;align-items:center;gap:5px;padding:6px 9px;border-radius:14px;font-size:10px;font-weight:800}.status-pill .v-icon{color:inherit}.status-pill--active{color:#11875e;background:#e7f8f0}.status-pill--blocked{color:#d43e4c;background:#ffebee}.identity-meta{display:flex;flex-wrap:wrap;gap:16px;margin-top:11px;color:#718597;font-size:11px}.identity-meta span{display:flex;align-items:center;gap:5px}.identity-meta .v-icon{color:#7691a4}.profile-details{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0;padding:18px 24px}.detail-group{display:grid;grid-template-columns:42px minmax(0,1fr);gap:12px;padding:3px 20px;border-right:1px solid #e7eef3}.detail-group:first-child{padding-left:0}.detail-group:last-child{padding-right:0;border-right:0}.detail-icon,.summary-icon{display:grid;place-items:center;border-radius:11px}.detail-icon{width:42px;height:42px}.detail-icon .v-icon,.summary-icon .v-icon{color:inherit}.detail-icon--teal,.summary-icon--teal{color:#078b87;background:#e7f7f5}.detail-icon--blue,.summary-icon--blue{color:#237fc0;background:#e9f4fc}.detail-icon--violet{color:#7857b4;background:#f1ecfb}.detail-label{display:block;margin:1px 0 7px;color:#294961;font-size:12px;font-weight:800}.detail-content dl{margin:0}.detail-content dl div{display:grid;grid-template-columns:minmax(100px,1fr) minmax(0,1.2fr);gap:8px;padding:3px 0;font-size:10px}.detail-content dt{color:#8797a5}.detail-content dd{margin:0;color:#405c72;font-weight:700;text-align:right;overflow-wrap:anywhere}.summary-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin:14px 0}.summary-card{display:flex;align-items:center;gap:12px;min-height:82px;padding:14px 16px;border:1px solid #e0e9ef;border-radius:12px;background:#fff}.summary-icon{flex:0 0 auto;width:44px;height:44px}.summary-icon--green{color:#169767;background:#e8f8ef}.summary-icon--red{color:#d94351;background:#ffebee}.summary-icon--orange{color:#d77a13;background:#fff3e2}.summary-card span,.summary-card strong{display:block}.summary-card span{color:#7c8d9c;font-size:10px}.summary-card strong{margin-top:3px;color:#23435c;font-size:21px}.summary-card .summary-status,.summary-card .summary-date{font-size:12px;line-height:1.3}.access-grid{display:grid;grid-template-columns:minmax(390px,.85fr) minmax(600px,1.4fr);gap:14px;align-items:start}.access-panel{overflow:hidden}.panel-heading{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:17px 19px;border-bottom:1px solid #e6edf2}.panel-heading h2{margin:2px 0;font-size:17px}.panel-heading p{font-size:10px}.count-badge{display:grid;place-items:center;min-width:34px;height:28px;padding:0 9px;border-radius:14px;color:#087d73;font-size:12px;font-weight:800;background:#e7f7f4}.count-badge--blue{color:#2176ad;background:#e8f3fb}.access-panel ::v-deep .v-data-table{border-radius:0;background:transparent}.access-panel ::v-deep .v-data-table__wrapper{overflow:auto}.access-panel ::v-deep table{min-width:560px;border-collapse:separate;border-spacing:0}.permissions-panel ::v-deep table{min-width:760px}.access-panel ::v-deep thead th{height:42px!important;padding:0 13px!important;color:#718395!important;font-size:9px!important;font-weight:800!important;text-transform:uppercase;background:#f7fafc;border-bottom:1px solid #e1e9ef!important}.access-panel ::v-deep tbody td{height:48px!important;max-width:240px;padding:8px 13px!important;color:#405b71;font-size:11px;border-bottom:1px solid #e8eef2!important;white-space:normal!important}.access-panel ::v-deep tbody tr:hover{background:#f7fbfc}.access-panel ::v-deep .v-data-footer{border-top:0;color:#718395}.access-panel ::v-deep .v-icon{color:#168b89}.access-panel ::v-deep .simple-th td:first-child{width:50px}.access-panel ::v-deep .simple-th td:first-child .v-icon{margin-left:4px!important}.access-panel ::v-deep tbody td:last-child:empty{display:none}
@media(max-width:1250px){.access-grid{grid-template-columns:1fr}.profile-details{grid-template-columns:1fr 1fr}.detail-group{border-right:0}.detail-group:last-child{grid-column:1/-1;margin-top:14px;padding:16px 0 0;border-top:1px solid #e7eef3}.summary-grid{grid-template-columns:1fr 1fr}}
@media(max-width:760px){.user-page{padding:12px}.page-heading,.identity-title{align-items:stretch;flex-direction:column}.heading-actions{display:grid;grid-template-columns:1fr}.profile-main{align-items:flex-start;padding:18px}.profile-details,.summary-grid{grid-template-columns:1fr}.detail-group,.detail-group:first-child,.detail-group:last-child{grid-column:auto;margin:0;padding:13px 0;border-top:1px solid #e7eef3;border-right:0}.detail-group:first-child{border-top:0}.identity-meta{flex-direction:column;gap:6px}.access-grid{grid-template-columns:minmax(0,1fr)}}
</style>
